import React, { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';

import { DataExplorerTable } from './DataExplorerTable';
import { DataExplorerLineChart } from './DataExplorerLineChart';
import { APIClient } from '../../utils/APIClient';
import { API_URL } from '../../settings/local_settings';
import { DataExplorerHeader } from './DataExplorerHeader';
import { Spinner } from '../Spinner';
import { FieldSensitivityService } from '../../utils/FieldSensitivityService';
import { ChartControls } from './ChartControls';
import DataExplorerJsonModal from './DataExplorerJsonModal';
import { ConfigureEventsModal } from './ConfigureEventsModal';
import { DataExplorerUpgradePage } from './DataExplorerUpgradePage';
import { DataExplorerChartsSection } from './DataExplorerChartsSection';
import { AccessService } from '../../utils/AccessService';
import { LocalStorage } from '../../utils/LocalStorage';
import moment from 'moment';
import { exportReportToPDF } from '../../utils/pdfExport';
import { DataExplorerToolbar } from './DataExplorerToolbar';
import { encodeFiltersToUrl } from '../../utils/DataExplorerUrlParams';
import { toast } from 'react-toastify';
import { getPeriodName } from '../../periods';
import { getDataByDataSourceType } from '../../nodeToGSheet';
import { FaIcon } from '../FaIcon';

class DataExplorerPageComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasAccess: null,
            loading: false,
            results: null,
            isDownloading: false,
            fieldsData: null,
            chartMetrics: [null, null], // [metric1, metric2] - hasta 2 métricas para el gráfico
            chartTimePeriod: null, // Período de tiempo seleccionado para el gráfico
            availableTimePeriods: [], // Períodos disponibles que vienen del backend
            defaultTimePeriod: null, // Período por defecto que viene del backend
            sourceType: null, // Necesario para ChartControls
            lastFilters: null, // Guardar los últimos filtros para poder hacer pedidos adicionales
            chartDataCache: {}, // Cache de datos del gráfico por período: { [period]: chartData }
            isDataNodeJsonModalOpen: false,
            dataNodeRequestJson: null,
            showTooltip: true, // Toggle para mostrar/ocultar tooltip al hover sobre nodos del gráfico
            showEventsConfigModal: false,
            unconfiguredAccounts: [],
            pendingExploreFilters: null,
        };
        // Variable de instancia para tracking de requests (no state para evitar race conditions)
        this.currentSearchRequestId = 0;
        this.handleExplore = this.handleExplore.bind(this);
        this.handleDownload = this.handleDownload.bind(this);
        this.handleDownloadPDF = this.handleDownloadPDF.bind(this);
        this.handleFieldsDataLoaded = this.handleFieldsDataLoaded.bind(this);
        this.handleChartMetricsChange = this.handleChartMetricsChange.bind(this);
        this.handleChartTimePeriodChange = this.handleChartTimePeriodChange.bind(this);
        this.handleExportDataNodeJson = this.handleExportDataNodeJson.bind(this);
        this.handleShowTooltipChange = this.handleShowTooltipChange.bind(this);
        this.handleShareUrl = this.handleShareUrl.bind(this);
        // Refs para exportación PDF
        this.lineChartRef = createRef();
        this.tableRef = createRef();
        this.chartsSectionRef = createRef();
    }

    componentDidMount() {
        const userId = LocalStorage.fetchUserId();
        AccessService.GetUserModules(userId, (data) => {
            const modules = data.modules || [];
            this.setState({ hasAccess: modules.includes(AccessService.MODULE_DATA_EXPLORER) });
        });

        // Cargar fieldsData desde el mismo endpoint que DataExplorerHeader
        new APIClient(API_URL + 'node-to-gsheet/sync-configuration/form-data').call('GET')
            .then(data => {
                if (data.fields_data) {
                    this.setState({ fieldsData: data.fields_data });
                }
            })
            .catch(() => {
                // Silenciar error, simplemente no cargar fieldsData
            });
    }

    handleFieldsDataLoaded(fieldsData) {
        this.setState({ fieldsData });
    }

    showErrorAlert(errorMessage) {
        const { t } = this.props;
        const prefix = t('data_explorer.error_prefix');
        alert(prefix + errorMessage);
    }

    handleChartMetricsChange(metrics) {
        const { results } = this.state;

        // Guardar preferencia en localStorage
        const prefs = LocalStorage.getDataExplorerChartPreferences() || {};
        LocalStorage.saveDataExplorerChartPreferences({ ...prefs, lineChartMetrics: metrics });

        // Verificar si las métricas seleccionadas ya tienen datos disponibles
        const hasDataForMetrics = this.checkMetricsHaveData(metrics, results);

        if (hasDataForMetrics) {
            // Si ya hay datos, solo actualizar el estado (el gráfico se refrescará automáticamente)
            this.setState({ chartMetrics: metrics });
        } else {
            // Si no hay datos, hacer un pedido al backend para obtenerlos
            this.setState({ chartMetrics: metrics });
            this.fetchChartMetricsData(metrics);
        }
    }

    checkMetricsHaveData(metrics, results) {
        if (!results || !results.chartData || !results.chartData.series) {
            return false;
        }
        
        const availableMetricIds = new Set(results.chartData.series.map(s => s.name));
        const selectedMetricIds = metrics.filter(m => m !== null);
        
        // Verificar que todas las métricas seleccionadas tengan datos
        return selectedMetricIds.every(metricId => availableMetricIds.has(metricId));
    }

    fetchChartMetricsData(metrics) {
        const { results, lastFilters } = this.state;
        if (!results || !results.tableData || !lastFilters) {
            return; // No hay datos de tabla o filtros para hacer el pedido
        }

        // Hacer un pedido al backend para obtener datos de las métricas seleccionadas
        // que no están en la tabla actual
        const { t } = this.props;
        this.setState({ loading: true });

        const data = {
            'agency_client_id': lastFilters.agencyClientId,
            'configParams': {
                'clientId': lastFilters.agencyClientId,
                'dataSourceType': lastFilters.sourceType,
            },
            'dateRange': lastFilters.periodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.periodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.periodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.periodState.selectedPeriod },
            'fields': [
                ...lastFilters.dimensions.map(d => ({ name: d })),
                ...lastFilters.metrics.map(m => ({ name: m })),
                // Agregar las métricas del gráfico que no están en la tabla
                ...metrics.filter(m => m !== null && !lastFilters.metrics.includes(m)).map(m => ({ name: m })),
            ],
            'compare': lastFilters.compare,
            'dimensions_filters_json': lastFilters.dimensions_filters_json || [],
        };

        // Agregar métricas del gráfico y time period
        const chartMetrics = metrics.filter(m => m !== null);
        if (chartMetrics.length > 0) {
            data.chartMetrics = chartMetrics.map(m => ({ name: m }));
        }
        
        if (this.state.chartTimePeriod) {
            const availablePeriods = this.state.availableTimePeriods || [];
            if (availablePeriods.length > 0 && availablePeriods.includes(this.state.chartTimePeriod)) {
                data.chartTimePeriod = this.state.chartTimePeriod;
            } else if (this.state.defaultTimePeriod) {
                data.chartTimePeriod = this.state.defaultTimePeriod;
            }
        } else if (this.state.defaultTimePeriod) {
            data.chartTimePeriod = this.state.defaultTimePeriod;
        }

        if (lastFilters.compare && lastFilters.comparePeriodState) {
            data.compareDateRange = lastFilters.comparePeriodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.comparePeriodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.comparePeriodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.comparePeriodState.selectedPeriod };
        }

        new APIClient(API_URL + 'data-explorer/query/').call('POST', data)
            .then(async response => {
                // Verificar si response es null o undefined
                if (!response) {
                    this.setState({ loading: false });
                    this.showErrorAlert(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    return;
                }

                if (response.status === 'ERROR') {
                    if (response.error_message === 'DATA_NODE_DATA_IS_PREPARING_OR_NOT_CONNECTION_TO_ACCOUNTS') {
                        this.showErrorAlert(t('data_explorer.data_preparing'));
                    } else if (response.error_message === 'NO_ACCESS') {
                        this.showErrorAlert(t('data_explorer.no_access', 'This module is not available for your subscription.'));
                    } else {
                        // Mostrar el mensaje de error específico del backend
                        this.showErrorAlert(response.error_message || t('data_explorer.error_fetching_data'));
                    }
                    this.setState({ loading: false });
                } else {
                    // Actualizar solo chartData, manteniendo el resto de los resultados
                    const newState = { 
                        loading: false 
                    };
                    
                    if (response.chartData) {
                        // Combinar las series existentes con las nuevas
                        const currentResults = this.state.results;
                        if (currentResults && currentResults.chartData && currentResults.chartData.series) {
                            // Crear un mapa de series existentes por label (clave única)
                            const existingSeriesMap = new Map();
                            currentResults.chartData.series.forEach(serie => {
                                // Usar label como clave única (puede haber múltiples series con el mismo name pero diferentes labels)
                                const key = serie.label || `${serie.name}-default`;
                                existingSeriesMap.set(key, serie);
                            });
                            
                            // Agregar nuevas series o actualizar existentes
                            response.chartData.series.forEach(serie => {
                                const key = serie.label || `${serie.name}-default`;
                                existingSeriesMap.set(key, serie);
                            });
                            
                            // Actualizar chartData con todas las series
                            newState.results = {
                                ...currentResults,
                                chartData: {
                                    ...response.chartData,
                                    series: Array.from(existingSeriesMap.values()),
                                    // Combinar metrics también
                                    metrics: response.chartData.metrics && currentResults.chartData.metrics
                                        ? this.mergeMetrics(currentResults.chartData.metrics, response.chartData.metrics)
                                        : (response.chartData.metrics || currentResults.chartData.metrics),
                                    // Mantener labels del chartData nuevo
                                    labels: response.chartData.labels || currentResults.chartData.labels
                                }
                            };
                        } else {
                            // Si no hay datos previos, usar los nuevos directamente
                            newState.results = {
                                ...this.state.results,
                                chartData: response.chartData
                            };
                        }
                    }
                    
                    this.setState(newState);
                }
            })
            .catch((error) => {
                this.setState({ loading: false });
                
                // Intentar extraer el mensaje de error si está disponible
                let errorMessage = t('data_explorer.error_fetching_data', 'Error fetching data.');
                if (error && error.error_message) {
                    errorMessage = error.error_message;
                } else if (error && error.message) {
                    errorMessage = error.message;
                } else if (error && typeof error === 'string') {
                    errorMessage = error;
                }
                
                this.showErrorAlert(errorMessage);
            });
    }

    mergeMetrics(existingMetrics, newMetrics) {
        // Combinar arrays de métricas, evitando duplicados por name
        const metricsMap = new Map();
        existingMetrics.forEach(m => {
            if (m && m.name) {
                metricsMap.set(m.name, m);
            }
        });
        newMetrics.forEach(m => {
            if (m && m.name) {
                metricsMap.set(m.name, m);
            }
        });
        return Array.from(metricsMap.values());
    }

    hasMetricFilters() {
        const { lastFilters, fieldsData } = this.state;
        if (!lastFilters || !lastFilters.dimensions_filters_json || !fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_SEGMENTS) {
            return false;
        }

        // Verificar si hay algún filtro sobre una métrica
        const filters = lastFilters.dimensions_filters_json;
        for (let i = 0; i < filters.length; i++) {
            const filterGroup = filters[i];
            if (Array.isArray(filterGroup)) {
                for (let j = 0; j < filterGroup.length; j++) {
                    const filter = filterGroup[j];
                    if (filter && filter.fieldName) {
                        const field = fieldsData.FIELD_DATA_BY_ID[filter.fieldName];
                        if (field && field.segment === fieldsData.FIELD_SEGMENTS.METRICS) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    hasData() {
        const { results } = this.state;
        return results && results.tableData && results.tableData.rows && results.tableData.rows.length > 0;
    }

    getFieldLabel(fieldId) {
        const { fieldsData } = this.state;
        if (!fieldId || !fieldsData || !fieldsData.FIELD_DATA_BY_ID) {
            return fieldId;
        }
        return fieldsData.FIELD_DATA_BY_ID[fieldId]?.name || fieldId;
    }

    getRowsCount() {
        const { results } = this.state;
        return results?.tableData?.rows?.length || 0;
    }

    getSelectedScopeLabel(filters) {
        const { t } = this.props;
        if (!filters) {
            return '';
        }

        if (!filters.selectedItems || filters.selectedItems.length === 0) {
            return filters.sourceType === 'AD_ACCOUNTS'
                ? t('data_explorer.all_ad_accounts', 'Todas las cuentas')
                : t('data_explorer.all_business_units', 'Todas las líneas de control');
        }

        return filters.sourceType === 'AD_ACCOUNTS'
            ? t('data_explorer.n_ad_accounts_selected', { count: filters.selectedItems.length })
            : t('data_explorer.n_business_units_selected', { count: filters.selectedItems.length });
    }

    getPeriodLabel(periodState) {
        const { t } = this.props;
        if (!periodState || !periodState.selectedPeriod) {
            return '';
        }

        if (periodState.selectedPeriod === 'CUSTOM') {
            const from = periodState.range?.from ? moment(periodState.range.from).format('DD MMM YYYY') : '';
            const to = periodState.range?.to ? moment(periodState.range.to).format('DD MMM YYYY') : '';
            return from && to ? `${from} - ${to}` : t('data_explorer.custom_range', 'Rango personalizado');
        }

        return getPeriodName(periodState.selectedPeriod, t);
    }

    renderEmptyState(type) {
        const { t } = this.props;
        const isNoResults = type === 'no-results';

        return (
            <div className={`data-explorer-empty-state ${isNoResults ? '-no-results' : ''}`}>
                <span className="data-explorer-empty-state__icon">
                    <FaIcon iconName={isNoResults ? 'filter' : 'table-cells-large'} />
                </span>
                <h3>
                    {isNoResults
                        ? t('data_explorer.client_profile.no_results_title', 'No encontramos resultados')
                        : t('data_explorer.client_profile.empty_title', 'Prepará una exploración')}
                </h3>
                <p>
                    {isNoResults
                        ? t('data_explorer.no_results', 'No hay resultados para la exploración realizada.')
                        : t('data_explorer.client_profile.empty_description', 'Elegí columnas, período y fuente para visualizar la información del cliente.')}
                </p>
            </div>
        );
    }

    renderResultsOverview() {
        if (!this.hasData()) {
            return null;
        }

        const { t } = this.props;
        const { lastFilters } = this.state;
        const sourceLabel = lastFilters?.sourceType
            ? getDataByDataSourceType(t)[lastFilters.sourceType]
            : '';
        const dimensionsCount = lastFilters?.dimensions?.length || 0;
        const metricsCount = lastFilters?.metrics?.length || 0;
        const columnsCount = dimensionsCount + metricsCount;
        const firstDimensions = (lastFilters?.dimensions || []).slice(0, 2).map(field => this.getFieldLabel(field));
        const firstMetrics = (lastFilters?.metrics || []).slice(0, 2).map(field => this.getFieldLabel(field));

        return (
            <div className="data-explorer-results-summary">
                <div className="data-explorer-results-summary__main">
                    <span className="data-explorer-results-summary__icon">
                        <FaIcon iconName="magnifying-glass-chart" />
                    </span>
                    <div>
                        <h3>{t('data_explorer.client_profile.results_title', 'Resultados')}</h3>
                        <div className="data-explorer-results-summary__meta">
                            <span>{t('data_explorer.client_profile.rows_count', { count: this.getRowsCount() })}</span>
                            <span>{t('data_explorer.client_profile.columns_count', { count: columnsCount })}</span>
                            {sourceLabel && <span>{sourceLabel}</span>}
                        </div>
                    </div>
                </div>
                <div className="data-explorer-results-summary__chips">
                    <span>{this.getSelectedScopeLabel(lastFilters)}</span>
                    <span>{this.getPeriodLabel(lastFilters?.periodState)}</span>
                    {lastFilters?.compare && (
                        <span>{t('data_explorer.client_profile.comparison_active', 'Comparación activa')}</span>
                    )}
                    {firstDimensions.length > 0 && (
                        <span>{firstDimensions.join(', ')}</span>
                    )}
                    {firstMetrics.length > 0 && (
                        <span>{firstMetrics.join(', ')}</span>
                    )}
                </div>
                <DataExplorerToolbar
                    onDownloadPDF={this.handleDownloadPDF}
                    onDownloadCSV={this.handleDownload}
                    onExportDataNodeJson={this.handleExportDataNodeJson}
                    onShareUrl={this.handleShareUrl}
                />
            </div>
        );
    }

    handleChartTimePeriodChange(timePeriod) {
        // Validar que el período esté en la lista de disponibles
        const availablePeriods = this.state.availableTimePeriods || [];
        if (availablePeriods.length > 0 && !availablePeriods.includes(timePeriod)) {
            // Si el período no está disponible, usar el default
            const defaultPeriod = this.state.defaultTimePeriod;
            if (defaultPeriod) {
                this.setState({ chartTimePeriod: defaultPeriod });
            }
            return;
        }
        
        // Si el período es el mismo que el actual, no hacer nada
        if (this.state.chartTimePeriod === timePeriod) {
            return;
        }
        
        // Verificar si ya tenemos datos en cache para este período
        const cachedChartData = this.state.chartDataCache && this.state.chartDataCache[timePeriod];
        
        if (cachedChartData && this.state.results) {
            // Si tenemos datos en cache, actualizar solo el chartData sin hacer pedido al backend
            this.setState({
                chartTimePeriod: timePeriod,
                results: {
                    ...this.state.results,
                    chartData: cachedChartData
                }
            });
        } else {
            // Si no tenemos datos en cache, hacer pedido al backend
            this.setState({ chartTimePeriod: timePeriod });
            this.fetchChartTimePeriodData(timePeriod);
        }
    }

    fetchChartTimePeriodData(timePeriod) {
        const { results, lastFilters } = this.state;
        if (!results || !results.tableData || !lastFilters) {
            return; // No hay datos de tabla o filtros para hacer el pedido
        }

        // Hacer un pedido al backend para obtener datos con el nuevo período
        const { t } = this.props;
        this.setState({ loading: true });

        const data = {
            'agency_client_id': lastFilters.agencyClientId,
            'configParams': {
                'clientId': lastFilters.agencyClientId,
                'dataSourceType': lastFilters.sourceType,
            },
            'dateRange': lastFilters.periodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.periodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.periodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.periodState.selectedPeriod },
            'fields': [
                ...lastFilters.dimensions.map(d => ({ name: d })),
                ...lastFilters.metrics.map(m => ({ name: m })),
            ],
            'compare': lastFilters.compare,
            'dimensions_filters_json': lastFilters.dimensions_filters_json || [],
        };

        // Agregar métricas del gráfico y el nuevo time period
        const chartMetrics = this.state.chartMetrics.filter(m => m !== null);
        if (chartMetrics.length > 0) {
            data.chartMetrics = chartMetrics.map(m => ({ name: m }));
        }
        
        data.chartTimePeriod = timePeriod;

        if (lastFilters.compare && lastFilters.comparePeriodState) {
            data.compareDateRange = lastFilters.comparePeriodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(lastFilters.comparePeriodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(lastFilters.comparePeriodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': lastFilters.comparePeriodState.selectedPeriod };
        }

        new APIClient(API_URL + 'data-explorer/query/').call('POST', data)
            .then(async response => {
                // Verificar si response es null o undefined
                if (!response) {
                    this.setState({ loading: false });
                    this.showErrorAlert(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    return;
                }

                if (response.status === 'ERROR') {
                    if (response.error_message === 'DATA_NODE_DATA_IS_PREPARING_OR_NOT_CONNECTION_TO_ACCOUNTS') {
                        this.showErrorAlert(t('data_explorer.data_preparing'));
                    } else if (response.error_message === 'NO_ACCESS') {
                        this.showErrorAlert(t('data_explorer.no_access', 'This module is not available for your subscription.'));
                    } else {
                        // Mostrar el mensaje de error específico del backend
                        this.showErrorAlert(response.error_message || t('data_explorer.error_fetching_data'));
                    }
                    this.setState({ loading: false });
                } else {
                    // Guardar los datos en cache para este período
                    const chartDataCache = { ...this.state.chartDataCache };
                    if (response.chartData) {
                        chartDataCache[timePeriod] = response.chartData;
                    }
                    
                    // Actualizar chartData con los nuevos datos
                    const newState = { 
                        loading: false,
                        chartDataCache
                    };
                    
                    if (response.chartData && this.state.results) {
                        newState.results = {
                            ...this.state.results,
                            chartData: response.chartData
                        };
                    }
                    
                    this.setState(newState);
                }
            })
            .catch((error) => {
                this.setState({ loading: false });
                
                // Intentar extraer el mensaje de error si está disponible
                let errorMessage = t('data_explorer.error_fetching_data', 'Error fetching data.');
                if (error && error.error_message) {
                    errorMessage = error.error_message;
                } else if (error && error.message) {
                    errorMessage = error.message;
                } else if (error && typeof error === 'string') {
                    errorMessage = error;
                }
                
                this.showErrorAlert(errorMessage);
            });
    }

    handleExplore(filters) {
        // Chequear si hay métricas que requieren eventos configurados
        if (filters.sourceType === 'AD_ACCOUNTS' && this.state.fieldsData) {
            const eventDependentFields = this.state.fieldsData.EVENT_DEPENDENT_FIELDS || [];
            const hasEventMetrics = filters.metrics.some(m => eventDependentFields.includes(m));

            if (hasEventMetrics) {
                new APIClient(`${API_URL}client/${filters.agencyClientId}/ad-accounts/events-check`)
                    .call('GET')
                    .then(response => {
                        if (response.data && response.data.needs_configuration) {
                            this.setState({
                                showEventsConfigModal: true,
                                unconfiguredAccounts: response.data.unconfigured_accounts,
                                pendingExploreFilters: filters,
                            });
                        } else {
                            this._executeExplore(filters);
                        }
                    })
                    .catch(() => {
                        this._executeExplore(filters);
                    });
                return;
            }
        }
        this._executeExplore(filters);
    }

    _executeExplore(filters) {
        // Incrementar ID de request sincrónicamente para evitar race conditions
        this.currentSearchRequestId += 1;
        const currentRequestId = this.currentSearchRequestId;

        this.setState({
            loading: true,
            lastFilters: filters,
            chartDataCache: {},
            chartMetrics: [null, null],
        }); // Limpiar cache y resetear métricas del gráfico al hacer nueva búsqueda
        const { t } = this.props;

        const data = {
            'agency_client_id': filters.agencyClientId,
            'configParams': {
                'clientId': filters.agencyClientId,
                'dataSourceType': filters.sourceType,
            },
            'dateRange': filters.periodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(filters.periodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(filters.periodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': filters.periodState.selectedPeriod },
            'fields': [
                ...filters.dimensions.map(d => ({ name: d })),
                ...filters.metrics.map(m => ({ name: m })),
            ],
            'compare': filters.compare,
            'dimensions_filters_json': filters.dimensions_filters_json || [],
        };

        // No agregar métricas del gráfico en una nueva búsqueda (se resetean)
        // El time period se mantiene si está disponible
        if (this.state.chartTimePeriod) {
            const availablePeriods = this.state.availableTimePeriods || [];
            if (availablePeriods.length > 0 && availablePeriods.includes(this.state.chartTimePeriod)) {
                data.chartTimePeriod = this.state.chartTimePeriod;
            } else if (this.state.defaultTimePeriod) {
                // Si el período seleccionado no está disponible, usar el default
                data.chartTimePeriod = this.state.defaultTimePeriod;
            }
        } else if (this.state.defaultTimePeriod) {
            // Si no hay período seleccionado pero hay default, usar el default
            data.chartTimePeriod = this.state.defaultTimePeriod;
        }

        // Guardar sourceType para ChartControls
        this.setState({ sourceType: filters.sourceType });

        // Agregar el período de comparación si está habilitado
        if (filters.compare && filters.comparePeriodState) {
            data.compareDateRange = filters.comparePeriodState.selectedPeriod === 'CUSTOM' ? {
                'startDate': moment(filters.comparePeriodState.range.from).format('YYYY-MM-DD'),
                'endDate': moment(filters.comparePeriodState.range.to).format('YYYY-MM-DD'),
            } : { 'predefinedPeriod': filters.comparePeriodState.selectedPeriod };
        }

        new APIClient(API_URL + 'data-explorer/query/').call('POST', data)
            .then(async response => {
                // Ignorar respuesta si ya hay una búsqueda más reciente
                if (this.currentSearchRequestId !== currentRequestId) {
                    return;
                }

                // Verificar si response es null o undefined (puede pasar si hay un error de conexión)
                if (!response) {
                    this.setState({ loading: false, results: null });
                    this.showErrorAlert(t('data_explorer.error_fetching_data', 'Error fetching data.'));
                    return;
                }

                // Verificar si es un error (puede venir como status === 'ERROR' o error_code === 409, etc.)
                if (response.status === 'ERROR' || response.error_code === 409 || (response.error_message && !response.tableData)) {
                    // Handle specific error messages
                    if (response.error_message === 'DATA_PREPARING') {
                        this.showErrorAlert(t('data_explorer.data_preparing'));
                    } else if (response.error_message === 'NO_CONECTION_TO_ACCOUNT') {
                        this.showErrorAlert(t('data_explorer.no_access', 'This module is not available for your subscription.'));
                    } else {
                        // Mostrar el mensaje de error específico del backend
                        const errorMessage = response.error_message || t('data_explorer.error_fetching_data');
                        this.showErrorAlert(errorMessage);
                    }
                    this.setState({ loading: false, results: null });
                } else {
                    // Cargar sensibilidades ANTES de actualizar el estado con los resultados
                    // Esto evita cambios de color visibles después de la carga
                    if (response.tableData && response.tableData.headers) {
                        const metricFields = response.tableData.headers
                            .filter(header => header.key && header.key.startsWith('metric_'))
                            .map(header => header.key.replace(/^metric_/, ''));
                        
                        if (metricFields.length > 0) {
                            try {
                                // Precargar sensibilidades antes de renderizar
                                await FieldSensitivityService.loadSensitivities(metricFields);
                            } catch (error) {
                                // Silenciar error, simplemente no precargar sensibilidades
                                console.error('Error precargando sensibilidades:', error);
                            }
                        }
                    }
                    
                    // Store the full response including chartData, summary, and dateRangeInfo
                    // También actualizar availableTimePeriods y defaultTimePeriod si vienen en la respuesta
                    const newState = { 
                        results: response, 
                        loading: false,
                        dataNodeRequestJson: response.dataNodeRequest
                            ? JSON.stringify(response.dataNodeRequest, null, 2)
                            : null
                    };
                    
                    if (response.chartTimePeriods) {
                        newState.availableTimePeriods = response.chartTimePeriods.available || [];
                        newState.defaultTimePeriod = response.chartTimePeriods.default || null;
                        // Si no hay time period seleccionado, usar el default
                        if (!this.state.chartTimePeriod && newState.defaultTimePeriod) {
                            newState.chartTimePeriod = newState.defaultTimePeriod;
                        }
                        // Validar que el período seleccionado siga estando disponible
                        // Si no está disponible, usar el default
                        if (this.state.chartTimePeriod && newState.availableTimePeriods.length > 0) {
                            if (!newState.availableTimePeriods.includes(this.state.chartTimePeriod)) {
                                newState.chartTimePeriod = newState.defaultTimePeriod;
                            }
                        }
                    }
                    
                    // Actualizar métricas del gráfico desde chartData.metrics si está disponible
                    // chartData.metrics ahora es un array de objetos {name, is_default}
                    if (response.chartData && response.chartData.metrics && Array.isArray(response.chartData.metrics)) {
                        // Si no hay métricas seleccionadas, intentar restaurar desde localStorage
                        const currentMetrics = this.state.chartMetrics || [null, null];
                        const hasSelectedMetrics = currentMetrics.some(m => m !== null);

                        if (!hasSelectedMetrics) {
                            const availableNames = response.chartData.metrics.map(m => m.name);
                            const prefs = LocalStorage.getDataExplorerChartPreferences();
                            const savedLineMetrics = prefs && prefs.lineChartMetrics;
                            const validSaved = savedLineMetrics
                                ? savedLineMetrics.map(m => m && availableNames.includes(m) ? m : null)
                                : null;

                            if (validSaved && validSaved.some(m => m !== null)) {
                                newState.chartMetrics = validSaved.slice(0, 2);
                            } else {
                                // Usar las métricas por defecto
                                const defaultMetrics = response.chartData.metrics
                                    .filter(m => m.is_default)
                                    .map(m => m.name)
                                    .slice(0, 2);

                                // Rellenar con null si hay menos de 2 métricas
                                while (defaultMetrics.length < 2) {
                                    defaultMetrics.push(null);
                                }
                                newState.chartMetrics = defaultMetrics.slice(0, 2);
                            }
                        }
                        // Si ya hay métricas seleccionadas, mantenerlas (no sobrescribir)
                    }
                    
                    // Guardar los datos del gráfico en cache para el período actual
                    if (response.chartData && newState.chartTimePeriod) {
                        const chartDataCache = { ...this.state.chartDataCache };
                        chartDataCache[newState.chartTimePeriod] = response.chartData;
                        newState.chartDataCache = chartDataCache;
                    }
                    
                    this.setState(newState);
                }
            })
            .catch((error) => {
                // Ignorar error si ya hay una búsqueda más reciente
                if (this.currentSearchRequestId !== currentRequestId) {
                    return;
                }
                this.setState({ loading: false, results: null });
                
                // Intentar extraer el mensaje de error si está disponible
                let errorMessage = t('data_explorer.error_fetching_data', 'Error fetching data.');
                if (error && error.error_message) {
                    errorMessage = error.error_message;
                } else if (error && error.message) {
                    errorMessage = error.message;
                } else if (error && typeof error === 'string') {
                    errorMessage = error;
                }
                
                this.showErrorAlert(errorMessage);
            });
    }

    handleExportDataNodeJson() {
        if (this.state.dataNodeRequestJson) {
            this.setState({ isDataNodeJsonModalOpen: true });
        }
    }

    handleShowTooltipChange(showTooltip) {
        this.setState({ showTooltip });
    }

    handleDownload() {
        this.setState({ isDownloading: true });
        const { results } = this.state;
        if (!results || !results.tableData) {
            this.setState({ isDownloading: false });
            return;
        }

        const { headers, rows } = results.tableData;
        
        // Extract header labels
        const headerLabels = headers.map(h => h.label);
        
        // Helper function to get formatted value for CSV
        const getFormattedValueForCSV = (value, header) => {
            // Handle metric values with comparison data - use formattedCurrentValue
            if (typeof value === 'object' && value !== null && value.formattedCurrentValue !== undefined) {
                return value.formattedCurrentValue !== null ? value.formattedCurrentValue : '';
            }
            
            // Handle formatted value objects (dimensions and metrics without comparison)
            if (typeof value === 'object' && value !== null && value.formattedValue !== undefined) {
                return value.formattedValue !== null ? value.formattedValue : '';
            }
            
            // Handle simple values
            if (value === null || value === undefined) {
                return '';
            }
            
            // Convert to string
            return String(value);
        };
        
        // Convert rows to CSV format
        const csvRows = rows.map(row => {
            return headers.map(header => {
                const value = row[header.key];
                const formattedValue = getFormattedValueForCSV(value, header);
                
                // Escape commas, quotes, and newlines
                if (formattedValue.includes(',') || formattedValue.includes('"') || formattedValue.includes('\n')) {
                    return `"${formattedValue.replace(/"/g, '""')}"`;
                }
                
                return formattedValue;
            });
        });
        
        // Combine headers and rows
        const csvContent = [
            headerLabels.join(','),
            ...csvRows.map(row => row.join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', 'data-explorer.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.setState({ isDownloading: false });
    }

    handleDownloadPDF() {
        const { t } = this.props;
        const { lastFilters, fieldsData, results } = this.state;

        // Resolver nombre del cliente
        let clientName = '';
        if (lastFilters && lastFilters.agencyClients && lastFilters.agencyClientId) {
            const client = lastFilters.agencyClients.find(
                c => c.id.toString() === lastFilters.agencyClientId.toString()
            );
            if (client) clientName = client.name;
        }

        // Resolver date range
        let dateRange = '';
        if (lastFilters && lastFilters.periodState && lastFilters.periodState.selectedPeriod) {
            if (lastFilters.periodState.selectedPeriod === 'CUSTOM' && lastFilters.periodState.range) {
                const from = lastFilters.periodState.range.from
                    ? moment(lastFilters.periodState.range.from).format('DD MMM YYYY')
                    : '';
                const to = lastFilters.periodState.range.to
                    ? moment(lastFilters.periodState.range.to).format('DD MMM YYYY')
                    : '';
                dateRange = from && to ? `${from} - ${to}` : '';
            } else {
                dateRange = getPeriodName(lastFilters.periodState.selectedPeriod, t);
            }
        }

        const refs = {
            lineChartRef: this.lineChartRef,
            chartsSectionRef: this.chartsSectionRef,
        };
        const context = {
            clientName,
            dateRange,
            dimensions: lastFilters ? lastFilters.dimensions : [],
            metrics: lastFilters ? lastFilters.metrics : [],
            fieldsData,
        };
        const tableData = results ? {
            headers: results.tableData?.headers || [],
            rows: results.tableData?.rows || [],
            summary: results.summary || {},
            isComparison: results.dateRangeInfo?.isComparison || false,
        } : null;
        exportReportToPDF(refs, context, tableData);
    }

    handleShareUrl() {
        const { t } = this.props;
        const { lastFilters } = this.state;
        if (!lastFilters) return;

        const url = encodeFiltersToUrl(lastFilters);

        navigator.clipboard.writeText(url)
            .then(() => {
                toast.success(t('data_explorer.link_copied', 'Enlace copiado al portapapeles'));
            })
            .catch(() => {
                toast.error(t('data_explorer.link_copy_error', 'No se pudo copiar el enlace'));
            });
    }

    render() {
        if (this.state.hasAccess === false) {
            return <DataExplorerUpgradePage />;
        }

        if (this.state.hasAccess === null) {
            return (
                <section className="section-content">
                    <div className="loading-content">
                        <Spinner />
                    </div>
                </section>
            );
        }

        const pageClassName = `section-content data-explorer-page ${this.props.fixedClientId ? 'data-explorer-page--client-profile' : ''}`;

        return (
            <>
                <section className={pageClassName}>
                    <header className="section-header">
                        <DataExplorerHeader
                            onExplore={this.handleExplore}
                            onDownload={this.handleDownload}
                            isDownloading={this.state.isDownloading}
                            onFieldsDataLoaded={this.handleFieldsDataLoaded}
                            fixedClientId={this.props.fixedClientId}
                        />
                    </header>
                    <div className="section-body data-explorer-results-area" style={{ position: 'relative', minHeight: '400px' }}>
                        {this.props.fixedClientId ? this.renderResultsOverview() : (
                            this.hasData() && (
                                <DataExplorerToolbar
                                    onDownloadPDF={this.handleDownloadPDF}
                                    onDownloadCSV={this.handleDownload}
                                    onExportDataNodeJson={this.handleExportDataNodeJson}
                                    onShareUrl={this.handleShareUrl}
                                />
                            )
                        )}
                        <div style={{ position: 'relative', width: '100%' }}>
                            {this.state.loading && (
                                <div className="data-explorer-loading-overlay">
                                    <Spinner />
                                </div>
                            )}
                            {/* Verificar si hay datos en los resultados */}
                            {!this.state.loading && !this.state.results ? (
                                this.renderEmptyState('initial')
                            ) : !this.state.loading && this.state.results && !this.hasData() ? (
                                this.renderEmptyState('no-results')
                        ) : (
                            <>
                                {/* 1. Métricas clave + gráficos de barras/torta */}
                                <div ref={this.chartsSectionRef}>
                                    <DataExplorerChartsSection
                                        results={this.state.results}
                                        lastFilters={this.state.lastFilters}
                                        fieldsData={this.state.fieldsData}
                                    />
                                </div>
                                {/* 2. Controles + gráfico temporal */}
                                {this.state.fieldsData && this.state.sourceType && (!this.state.loading || this.state.results) && !this.hasMetricFilters() && (
                                    <ChartControls
                                        metrics={this.state.chartMetrics}
                                        timePeriod={this.state.chartTimePeriod}
                                        availableTimePeriods={this.state.availableTimePeriods}
                                        defaultTimePeriod={this.state.defaultTimePeriod}
                                        fieldsData={this.state.fieldsData}
                                        sourceType={this.state.sourceType}
                                        onMetricsChange={this.handleChartMetricsChange}
                                        onTimePeriodChange={this.handleChartTimePeriodChange}
                                        showTooltip={this.state.showTooltip}
                                        onShowTooltipChange={this.handleShowTooltipChange}
                                        availableMetrics={this.state.results && this.state.results.chartData && this.state.results.chartData.series
                                            ? [...new Set(this.state.results.chartData.series.map(s => s.name))]
                                            : []}
                                    />
                                )}
                                {this.hasMetricFilters() ? (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '100px',
                                        padding: '1rem',
                                        textAlign: 'center',
                                        color: '#666'
                                    }}>
                                        <p>{this.props.t('data_explorer.remove_metric_filters_for_chart', 'Para ver el gráfico se deben remover los filtros sobre métricas')}</p>
                                    </div>
                                ) : (
                                    this.state.results && (
                                        <div ref={this.lineChartRef}>
                                            <DataExplorerLineChart
                                                chartData={this.state.results.chartData}
                                                selectedMetrics={this.state.chartMetrics}
                                                showTooltip={this.state.showTooltip}
                                            />
                                        </div>
                                    )
                                )}
                                {/* 3. Tabla */}
                                <div ref={this.tableRef}>
                                    <DataExplorerTable
                                        loading={false}
                                        results={this.state.results}
                                        fieldsData={this.state.fieldsData}
                                    />
                                </div>
                            </>
                        )}
                        </div>
                    </div>
                </section>
                <DataExplorerJsonModal
                    isOpen={this.state.isDataNodeJsonModalOpen}
                    toggle={() => this.setState({ isDataNodeJsonModalOpen: false })}
                    jsonContent={this.state.dataNodeRequestJson || ''}
                />
                <ConfigureEventsModal
                    isOpen={this.state.showEventsConfigModal}
                    unconfiguredAccounts={this.state.unconfiguredAccounts}
                    onComplete={() => {
                        this.setState({ showEventsConfigModal: false });
                        this._executeExplore(this.state.pendingExploreFilters);
                    }}
                    toggle={() => this.setState({ showEventsConfigModal: false, pendingExploreFilters: null })}
                />
            </>
        );
    }
}

export const DataExplorerPage = withTranslation()(DataExplorerPageComponent);
